import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import {
  expectedDirection,
  LinkCashEventError,
  useLinkCashEventToInvoice,
  useUnmatchedCashTransactionsForInvoice,
} from '../api/use-link-cash-event';
import { useInvoicePayments } from '../api/use-invoice-payments';
import type { InvoiceRow, PaymentRow } from '../types';
import { AmountField, CandidateList, SubmitFooter } from './link-cash-event-candidates';

export interface LinkCashEventDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly invoice: InvoiceRow;
  readonly onLinked?: (() => void) | undefined;
}

const EPSILON = 0.01;

function parseAmount(value: string | null): number {
  const parsed = Number.parseFloat(value ?? '0');
  return Number.isFinite(parsed) ? parsed : 0;
}

function outstandingAmount(invoice: InvoiceRow, payments: readonly PaymentRow[]): number {
  const total = parseAmount(invoice.total_amount);
  const paid = payments.reduce((sum, p) => sum + parseAmount(p.total_amount_paid), 0);
  return Math.max(0, total - paid);
}

function defaultAmount(invoice: InvoiceRow, payments: readonly PaymentRow[]): string {
  const outstanding = outstandingAmount(invoice, payments);
  return outstanding > 0 ? outstanding.toFixed(2) : '0.00';
}

export function LinkCashEventDialog({
  open,
  onOpenChange,
  invoice,
  onLinked,
}: LinkCashEventDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Link cash event</DialogTitle>
          <DialogDescription>
            Pick an unmatched Airwallex transaction in {invoice.currency ?? 'this currency'} to
            apply against {invoice.invoice_number ?? `invoice ${invoice.id}`}.
          </DialogDescription>
        </DialogHeader>
        {open ? (
          <DialogBody invoice={invoice} onOpenChange={onOpenChange} onLinked={onLinked} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function DialogBody({
  invoice,
  onOpenChange,
  onLinked,
}: Readonly<{
  invoice: InvoiceRow;
  onOpenChange: (open: boolean) => void;
  onLinked?: (() => void) | undefined;
}>) {
  const direction = expectedDirection(invoice);
  if (direction === null || invoice.currency === null) {
    return (
      <p className="text-sm text-destructive" role="alert">
        Invoice direction or currency is missing — cannot match a cash event.
      </p>
    );
  }
  return (
    <LoadedDialogBody
      invoice={invoice}
      currency={invoice.currency}
      onOpenChange={onOpenChange}
      onLinked={onLinked}
    />
  );
}

function LoadedDialogBody(
  props: Readonly<{
    invoice: InvoiceRow;
    currency: string;
    onOpenChange: (open: boolean) => void;
    onLinked?: (() => void) | undefined;
  }>,
) {
  const cashQuery = useUnmatchedCashTransactionsForInvoice(props.invoice, true);
  const paymentsQuery = useInvoicePayments(props.invoice.id, true);

  if (cashQuery.isLoading || paymentsQuery.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (cashQuery.isError) {
    return (
      <p className="text-sm text-destructive" role="alert">
        Could not load cash transactions. {cashQuery.error.message}
      </p>
    );
  }
  if (paymentsQuery.isError) {
    return (
      <p className="text-sm text-destructive" role="alert">
        Could not load invoice payments. {paymentsQuery.error.message}
      </p>
    );
  }
  return (
    <ReadyDialogBody
      {...props}
      candidates={cashQuery.data ?? []}
      payments={paymentsQuery.data ?? []}
    />
  );
}

function ReadyDialogBody({
  invoice,
  currency,
  candidates,
  payments,
  onOpenChange,
  onLinked,
}: Readonly<{
  invoice: InvoiceRow;
  currency: string;
  candidates: readonly import('@/features/finance/cash-ledger.types').CashTransactionRow[];
  payments: readonly PaymentRow[];
  onOpenChange: (open: boolean) => void;
  onLinked?: (() => void) | undefined;
}>) {
  const mutation = useLinkCashEventToInvoice(invoice);
  const [selectedTxnId, setSelectedTxnId] = useState<string | null>(null);
  const initialAmount = useMemo(() => defaultAmount(invoice, payments), [invoice, payments]);
  const outstanding = useMemo(() => outstandingAmount(invoice, payments), [invoice, payments]);
  const [amount, setAmount] = useState<string>(initialAmount);

  useEffect(() => {
    setAmount(initialAmount);
  }, [initialAmount]);

  return (
    <LoadedForm
      invoice={invoice}
      currency={currency}
      candidates={candidates}
      selectedTxnId={selectedTxnId}
      onSelectTxn={setSelectedTxnId}
      amount={amount}
      onAmountChange={setAmount}
      isPending={mutation.isPending}
      onSubmit={makeSubmitHandler({
        selectedTxnId,
        invoice,
        amount,
        maxAmount: outstanding,
        mutateAsync: mutation.mutateAsync,
        onLinked,
        onOpenChange,
      })}
      onCancel={() => {
        onOpenChange(false);
      }}
    />
  );
}

interface LoadedFormProps {
  readonly invoice: InvoiceRow;
  readonly currency: string;
  readonly candidates: readonly import('@/features/finance/cash-ledger.types').CashTransactionRow[];
  readonly selectedTxnId: string | null;
  readonly onSelectTxn: (txnId: string) => void;
  readonly amount: string;
  readonly onAmountChange: (v: string) => void;
  readonly isPending: boolean;
  readonly onSubmit: () => void;
  readonly onCancel: () => void;
}

function LoadedForm(props: LoadedFormProps) {
  return (
    <>
      <CandidateList
        candidates={props.candidates}
        selectedTxnId={props.selectedTxnId}
        onSelect={props.onSelectTxn}
      />
      <AmountField value={props.amount} onChange={props.onAmountChange} currency={props.currency} />
      <SubmitFooter
        invoiceId={props.invoice.id}
        selectedTxnId={props.selectedTxnId}
        amount={props.amount}
        isPending={props.isPending}
        onCancel={props.onCancel}
        onSubmit={props.onSubmit}
      />
    </>
  );
}

function makeSubmitHandler(args: {
  selectedTxnId: string | null;
  invoice: InvoiceRow;
  amount: string;
  maxAmount: number;
  mutateAsync: ReturnType<typeof useLinkCashEventToInvoice>['mutateAsync'];
  onLinked?: (() => void) | undefined;
  onOpenChange: (open: boolean) => void;
}): () => void {
  return () => {
    void submit({
      txnId: args.selectedTxnId,
      invoiceId: args.invoice.id,
      amount: args.amount,
      maxAmount: args.maxAmount,
      mutateAsync: args.mutateAsync,
      onSuccess: () => {
        args.onLinked?.();
        args.onOpenChange(false);
      },
    });
  };
}

async function submit(args: {
  txnId: string | null;
  invoiceId: string;
  amount: string;
  maxAmount: number;
  mutateAsync: ReturnType<typeof useLinkCashEventToInvoice>['mutateAsync'];
  onSuccess: () => void;
}): Promise<void> {
  if (args.txnId === null) return;
  const amount = parseAmount(args.amount);
  if (amount <= 0 || amount > args.maxAmount + EPSILON) {
    toast.error(`Amount must be greater than 0 and no more than ${args.maxAmount.toFixed(2)}.`);
    return;
  }
  try {
    await args.mutateAsync({ txnId: args.txnId, invoiceId: args.invoiceId, amount: args.amount });
    toast.success('Cash event linked to invoice.');
    args.onSuccess();
  } catch (err) {
    const message = err instanceof LinkCashEventError ? err.message : 'Failed to link cash event.';
    toast.error(message);
  }
}
